import Constant from "../constants";
import Cookies from "js-cookie";
import { AsyncKey } from "constants/index";
import _ from "lodash";

const METHOD_GET = "get";
const METHOD_POST = "post";
const METHOD_PUT = "put";
const METHOD_DELETE = "delete";
const METHOD_PATCH = "patch";

async function requestAPI(
  method: string,
  uri: string,
  body?: object,
  serviceBaseUrl?: string,
  h?: any,
  isText?: boolean
) {
  // Build API header
  let headers: any = {
    Accept: "*/*",
    "Access-Control-Allow-Origin": "*",
  };
  if (body instanceof FormData) {
    // headers["Content-Type"] = "multipart/form-data";
    headers = {};
  } else {
    headers["Content-Type"] = "application/json";
  }

  // Build API url
  let apiUrl = "";
  if (serviceBaseUrl) {
    apiUrl = serviceBaseUrl + uri;
  } else {
    apiUrl = Constant.apiBaseUrl + uri;
  }

  // Get access token and attach it to API request's header
  let access_token = Cookies.get("access_token");
  if (access_token != null) {
    headers["Authorization"] = `Bearer ${access_token}`;
  } else {
    console.log("No token is stored");
  }

  let signal: AbortSignal | null = null;
  if (_.has(h, 'signal')) {
    signal = h.signal;
    console.log('Do request with abort signal', signal);
    _.unset(h, 'signal');
  }

  if (h) {
    headers = { ...headers, ...h };
  }

  let selectedLanguage = Cookies.get(AsyncKey.languageKey);
  if (selectedLanguage) {
    headers["Language"] = selectedLanguage;
  }

  // Build API body
  let content_body = null;
  if (
    method.toLowerCase() === METHOD_POST ||
    method.toLowerCase() === METHOD_PUT ||
    method.toLowerCase() === METHOD_DELETE ||
    method.toLowerCase() === METHOD_PATCH
  ) {
    if (body) {
      if (body instanceof FormData) {
        content_body = body;
      } else {
        content_body = JSON.stringify(body);
      }
    }
  }
  // Construct fetch options
  let fetchOptions = { method, headers, body: content_body, signal };
  // Run the fetching
  return fetch(apiUrl, fetchOptions)
    .then(res => {
      if (res.status >= 400) {
        const json = res.json();

        return Promise.reject({
          error: _.get(json, 'message', 'Whoops! Something went wrong'),
          statusCode: res.status,
        });
      }

      if (isText) {
        return res.text()
      }
  
      return res.json().then((data) => {
        return { ...data, statusCode: res.status };
      });
    })
    .catch((err) => {
      return {
        message: err.error || err.toString(),
        status: 'error',
        statusCode: 400,
      };
    });
}

const timeRequestMap: { [key: string]: any } = {};

const ApiCaller = {
  get(url: string, baseUrl?: string, headers?: {[key: string]: any}): Promise<any> {
    return requestAPI(METHOD_GET, url, undefined, baseUrl, headers);
  },

  post(
    url: string,
    data?: any,
    baseUrl?: string,
    headers?: any,
    isText?: boolean
  ): Promise<any> {
    return requestAPI(METHOD_POST, url, data, baseUrl, headers, isText);
  },

  patch(url: string, data?: any, baseUrl?: string, headers?: {[key: string]: any}): Promise<any> {
    return requestAPI(METHOD_PATCH, url, data, baseUrl, headers);
  },

  put(url: string, data?: any, baseUrl?: string, headers?: {[key: string]: any}): Promise<any> {
    return requestAPI(METHOD_PUT, url, data, baseUrl, headers);
  },

  delete(url: string, data?: any, baseUrl?: string, headers?: {[key: string]: any}): Promise<any> {
    return requestAPI(METHOD_DELETE, url, data, baseUrl, headers);
  },

  getWithLatestResponse(url: string, baseUrl?: string): Promise<any> {
    const currentTime = new Date().getTime();
    if (!timeRequestMap[url]) {
      timeRequestMap[url] = {
        requestTime: currentTime,
      };
    } else {
      timeRequestMap[url].requestTime = currentTime;
    }
    return new Promise((resolve) => {
      return requestAPI(METHOD_GET, url, undefined, baseUrl).then(
        (res: any) => {
          const { requestTime } = timeRequestMap[url] || {};
          if (requestTime != currentTime) {
            return resolve({ statusCode: 400, cancelled: true });
          }
          delete timeRequestMap[url];
          return resolve(res);
        }
      );
    });
  },

  postWithLatestResponse(
    url: string,
    data?: any,
    baseUrl?: string
  ): Promise<any> {
    const currentTime = new Date().getTime();
    if (!timeRequestMap[url]) {
      timeRequestMap[url] = {
        requestTime: currentTime,
      };
    } else {
      timeRequestMap[url].requestTime = currentTime;
    }
    return new Promise((resolve) => {
      return requestAPI(METHOD_POST, url, data, baseUrl).then((res: any) => {
        const { requestTime } = timeRequestMap[url] || {};
        if (requestTime !== currentTime) {
          return resolve({ statusCode: 400, cancelled: true });
        }
        delete timeRequestMap[url];
        return resolve(res);
      });
    });
  },
};

export default ApiCaller;
